import React, { useEffect, useState } from 'react';
import {
  Button,
  CondensedLarge,
  ContextMenu,
  ContextMenuDivider,
  ContextMenuHeader,
  Gutter,
  IconButton,
  Layout,
  LayoutItem,
  SectionHeader,
  useApi,
  Visible,
  useApiWithErrors,
  ApiError,
} from '@axiom/ui';
import type { Notification } from '@axiom/validation';

import { UserApi } from '../../api/user-api';
import { NotificationsApi } from '../../api/notifications-api';

import {
  NotificationsIconWrapper,
  NotificationsIconBadge,
} from './NotificationsStyles';
import { NotificationsItem } from './NotificationsItem';
import { useNotificationsRefresher } from './useNotificationsRefresher';

export type NotificationsProps = {};

export const Notifications = (props: NotificationsProps) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const [{ data: user }] = useApi(UserApi.readUser());
  const [notificationSearch] = useApiWithErrors(
    NotificationsApi.readNotifications(user.id)
  );
  useNotificationsRefresher(user.id);

  useEffect(() => {
    if (!(notificationSearch instanceof ApiError)) {
      setUnreadCount(notificationSearch._meta.totalUnreadCount);
      setNotifications(notificationSearch.data);
    }
  }, [notificationSearch]);

  return (
    <ContextMenu
      name="NOTIFICATIONS"
      direction="left"
      fullscreenOn="smallScreen"
      anchor={
        <Gutter right="16px">
          <NotificationsIconWrapper>
            <Visible only="smallScreen">
              <Button
                name="PENDO-NOTIFICATIONS-SMALL-SCREEN"
                icon="bell"
                variation="minimal"
                pattern="secondary"
              >
                Notifications
              </Button>
            </Visible>
            <Visible only="largeScreen">
              <IconButton
                name="PENDO-NOTIFICATIONS-LARGE-SCREEN"
                icon="bell"
                variation="minimal"
                pattern="secondary"
              />
            </Visible>
            {unreadCount > 0 && (
              <NotificationsIconBadge data-test="NOTIFICATIONS_ICON_BADGE" />
            )}
          </NotificationsIconWrapper>
        </Gutter>
      }
    >
      <ContextMenuHeader>
        <Gutter horizontal="4px" top="8px">
          <Layout position="middle" horizontalGutter="8px">
            <LayoutItem fluid>
              <SectionHeader>Notifications</SectionHeader>
            </LayoutItem>
            {notifications.length > 0 && (
              <Button
                onClick={async () => {
                  await NotificationsApi.updateNotificationsMarkAllAsRead(
                    user.id
                  );
                  await NotificationsApi.refreshNotifications(user.id);
                }}
                variation="minimal"
                pattern="secondary"
                name="NOTIFICATIONS_MARK_ALL_AS_READ"
              >
                Read All
              </Button>
            )}
            <Visible only="smallScreen">
              <IconButton
                name="NOTIFICATIONS_CLOSE_BUTTON"
                icon="x-mark"
                pattern="secondary"
                variation="minimal"
              />
            </Visible>
          </Layout>
        </Gutter>
      </ContextMenuHeader>
      <ContextMenuDivider />
      {notifications.length === 0 ? (
        <Gutter horizontal="16px" bottom="24px" name="NO_NOTIFICATIONS_MESSAGE">
          <Layout position="center">
            <CondensedLarge>No Notifications</CondensedLarge>
          </Layout>
        </Gutter>
      ) : (
        <Gutter name="NOTIFICATIONS_LIST" horizontal="16px" vertical="16px">
          <Layout direction="vertical" verticalGutter="16px">
            {notifications.map((item, index) => (
              <LayoutItem key={item.id}>
                <NotificationsItem
                  name={`NOTIFICATIONS_ITEM_${index}`}
                  notification={item}
                  user={user}
                />
              </LayoutItem>
            ))}
          </Layout>
        </Gutter>
      )}
      <ContextMenuDivider />
    </ContextMenu>
  );
};
